import { useEffect, useState } from 'react';

import type { FetchProjectDataProps, JiraProjects } from '../__types__/apiUtils';

import { fetchJiraProjects, fetchRecentJiraProjects } from './apiUtils';
import { useBulkCreateLoggingEvents } from './useBulkCreateLoggingEvents';
import { ErrorStates, useSetError } from '../providers/BulkCreateContextProvider';

export const FETCH_JIRA_PROJECTS = 'fetch-jira-projects';
export const FETCH_RECENT_JIRA_PROJECTS = 'fetch-recent-jira-projects';

export const useGetJiraProjects = ({ cloudId, query, onComplete }: FetchProjectDataProps) => {
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();
	const [recentProjects, setRecentProjects] = useState<JiraProjects | undefined>(undefined);
	const [projects, setProjects] = useState<JiraProjects | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	useEffect(() => {
		if (!cloudId) {
			setRecentProjects(undefined);
			setProjects(undefined);
			return;
		}
		const fetchProjects = async () => {
			setLoading(true);
			try {
				if (query) {
					const projectResult = await fetchJiraProjects({ cloudId, query });
					handleProjectData({ status: 'fulfilled', value: projectResult });
					onComplete?.(undefined, projectResult);
				} else {
					const [recentProjectResult, projectResult] = await Promise.allSettled([
						fetchRecentJiraProjects({ cloudId }),
						fetchJiraProjects({ cloudId, query }),
					]);
					const projectData = handleProjectData(projectResult);
					await handleRecentProjectData(recentProjectResult, projectData);
				}
			} finally {
				setLoading(false);
			}
		};

		const handleProjectData = (projectResult: PromiseSettledResult<JiraProjects | undefined>) => {
			if (projectResult.status === 'fulfilled') {
				setProjects(projectResult.value);
				return projectResult.value;
			} else {
				logRequestFailed({
					requestName: FETCH_JIRA_PROJECTS,
					errorMessage: `Failed to fetch projects: ${projectResult.reason}`,
				});
				void setError(ErrorStates.PROJECT_RETRIEVAL);
			}
		};

		const handleRecentProjectData = async (
			recentProjectResult: PromiseSettledResult<JiraProjects | undefined>,
			projectData: JiraProjects | undefined,
		) => {
			if (recentProjectResult.status === 'fulfilled') {
				const recentProjectData = recentProjectResult.value;
				const recentProjectIds = recentProjectData?.map((project) => project.id);
				if (recentProjectIds && recentProjectIds.length > 0) {
					await fetchAndSetRecentProjects(recentProjectIds, projectData);
				} else {
					setRecentProjects([]);
					onComplete?.([], projectData);
				}
			} else {
				logRequestFailed({
					requestName: FETCH_RECENT_JIRA_PROJECTS,
					errorMessage: `Failed to fetch recent projects: ${recentProjectResult.reason}`,
				});
				void setError(ErrorStates.PROJECT_RETRIEVAL);
				setRecentProjects([]);
				onComplete?.([], projectData);
			}
		};

		const fetchAndSetRecentProjects = async (
			recentProjectIds: string[],
			projectData: JiraProjects | undefined,
		) => {
			try {
				const recentProjectsWithIssueCreatePermissionData = await fetchJiraProjects({
					cloudId,
					query,
					projectIds: recentProjectIds,
				});
				setRecentProjects(recentProjectsWithIssueCreatePermissionData);
				onComplete?.(recentProjectsWithIssueCreatePermissionData, projectData);
			} catch (e) {
				logRequestFailed({
					requestName: FETCH_RECENT_JIRA_PROJECTS,
					errorMessage: `Failed to fetch recent projects with issue create permission: ${(e as Error).message}`,
				});
				void setError(ErrorStates.PROJECT_RETRIEVAL);
				onComplete?.([], projectData);
			}
		};
		void fetchProjects();
	}, [cloudId, query, onComplete, setRecentProjects, setProjects, logRequestFailed, setError]);
	return { recentProjects, projects, loading };
};
